<template>
  <div class="bg-canvas">
    <client-only>
      <SectionScript />
      <PopupDownload ref="popupDownloadRef" />
      <PopupShare ref="popupShareRef" />
      <template v-if="isGalleryTrip">
        <EnquiryCaptureMultiSteps :visible="multiStepsVisible" @close="handleVisibleSubmitEnquiryForm(false)" />
      </template>
      <template v-if="isBookableTrip">
        <PopupConfirmTravelDate ref="popupConfirmTravelDateRef" :designer="dataTrip.designer" />
        <PopupTripExpired ref="popupTripExpiredRef" />
        <PopupTerm ref="popupTermRef" />
      </template>
    </client-only>

    <div v-if="domain === 'agentpreview' ? loadingStatus && agentContactApiLoaded : loadingStatus">
      <div v-if="dataTrip">
        <template v-if="isBookableTrip">
          <SectionRateTripTop v-if="isCompleted" />
          <SectionCountdown v-else-if="dataTrip.valid_time && showCountDown" @handleBook="emitBook" @confirmDate="toggleModalConfirmDate(true)" />
        </template>
        <SectionBanner @share="toggleModalShare" @download="toggleModalDownload('trip-plan-actions-bar')" />
        <div class="container tl-px-32 mb-px-20" ref="containerRef">
          <BodyRightSideBar>
            <template #upper>
              <div class="relative z-4" :style="{ marginTop: `-${MARGIN_TOP_MOBILE}px` }" v-if="!isDesktop">
                <TripPriceBox
                  :bg-float-class="bgPriceBoxClass"
                  @customTrip="customTrip"
                  @handleBook="emitBook"
                  @openTermBox="toggleModalDJTerm(true)"
                  @share="toggleModalShare"
                  @scrollToInclusions="scrollToInclusions"
                  @confirmDate="toggleModalConfirmDate(true)"
                />
                <div class="mb-30" v-if="isCompleted && isBookableTrip">
                  <SectionRateTrip />
                </div>
              </div>
            </template>
            <template #body>
              <SectionDescription />
              <SectionReason />
              <SectionTripHighlights :highlights="dataTrip?.highlights" />
              <client-only>
                <SectionMap v-if="!dataTrip?.is_simplified" :locations="uniqueLocation" :mapNote="dataTrip?.map_note" />
              </client-only>
              <div ref="sectionTimeLineRef">
                <SectionTimeLine :showCustomButton="!isBookableTrip" :item-default-expand="ITEM_DEFAULT_EXPAND" @customise-trip="customTrip" />
              </div>
              <SectionBrochure @open-download="toggleModalDownload('section-brochure')" />
              <div ref="sectionInclusionsRef">
                <SectionInclusions v-if="domain === 'preview' || domain === 'traveller'" />
              </div>
            </template>
            <template v-if="isDesktop" #sidebar>
              <div
                class="relative z-2 pb-32"
                :style="{
                  height: !showPopupClientFeedback ? `calc(100% + ${MARGIN_TOP}px)` : `${topSectionEnquiryForm + MARGIN_TOP}px`,
                  'margin-top': `-${MARGIN_TOP}px`,
                }"
              >
                <div class="top-100 sticky">
                  <TripPriceBox
                    @customTrip="customTrip"
                    @scrollToInclusions="scrollToInclusions"
                    @handleBook="emitBook"
                    @openTermBox="toggleModalDJTerm(true)"
                    @confirmDate="toggleModalConfirmDate(true)"
                  />
                  <div class="mb-30" v-if="isCompleted">
                    <SectionRateTrip />
                  </div>
                </div>
              </div>
              <client-only>
                <div v-if="isGalleryTrip" class="sticky top-100">
                  <SectionEnquiryForm :visible="true" />
                </div>
                <div v-else-if="showPopupClientFeedback && isBookableTrip" class="sticky top-100">
                  <SectionClientFeedback :designer="dataTrip.designer" :enquiryID="dataTrip.enquiry_id" :tripID="dataTrip.id" />
                </div>
              </client-only>
            </template>
          </BodyRightSideBar>
          <client-only>
            <div id="detailed-review">
              <SectionDetailedReview ref="sectionDetailedReviewRef" :designer="dataTrip.designer" />
            </div>
          </client-only>
        </div>
        <div v-if="isGalleryTrip" class="bg-cream-deep">
          <div class="container tl-px-32 mb-px-20">
            <client-only>
              <SectionSuggestTrips heapSource="suggest-trip-plan" title="More Extraordinary Trips" />
              <SectionSuggestLocalDesigner />
            </client-only>
            <SectionInspiration :data-trip="dataTrip" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from "vue";
import { useRouter, useRoute } from 'vue-router'
import BodyRightSideBar from '~/components/globals/layout/BodyRightSideBar/BodyRightSideBar'
import EnquiryCaptureMultiSteps from '~/components/pages/Enquiry/MultiStepsForm/Sections/EnquiryCaptureMultiSteps.vue'
import onlineStatus from '~/composables/api/onlineStatus'
import tripApi from '~/composables/api/tripApi'
import { DESKTOP, PHONE } from '~/constant/common'
import { daysBetween, isDeepEqual, publicDomain, scrollToHash, findExchangeRate, formatDate } from '~/helper'
import { debounceAmplitudeScriptTracking } from '~/mixins/amplitude'
import { debounceHeapTracking } from '~/mixins/heap'
import SectionBanner from './sections/SectionBanner/SectionBanner.vue'
import SectionBrochure from './sections/SectionBrochure/SectionBrochure.vue'
import SectionClientFeedback from './sections/SectionClientFeedback/SectionClientFeedback'
import SectionCountdown from './sections/SectionCountdown/SectionCountdown.vue'
import SectionDescription from './sections/SectionDescription/SectionDescription.vue'
import SectionDetailedReview from './sections/SectionDetailedReview/SectionDetailedReview.vue'
import SectionEnquiryForm from './sections/SectionEnquiryForm/SectionEnquiryForm'
import SectionInclusions from './sections/SectionInclusions/SectionInclusions.vue'
import SectionInspiration from './sections/SectionInspiration/SectionInspiration'
import SectionMap from './sections/SectionMap/SectionMap.vue'
import PopupConfirmTravelDate from './sections/SectionPopup/PopupConfirmTravelDate'
import PopupDownload from './sections/SectionPopup/PopupDownload'
import PopupShare from './sections/SectionPopup/PopupShare'
import PopupTerm from './sections/SectionPopup/PopupTerm'
import PopupTripExpired from './sections/SectionPopup/PopupTripExpired'
import SectionRateTrip from './sections/SectionRateTrip/SectionRateTrip'
import SectionRateTripTop from './sections/SectionRateTrip/SectionRateTripTop'
import SectionReason from './sections/SectionReason/SectionReason.vue'
import SectionScript from './sections/SectionScript/SectionScript'
import SectionSuggestLocalDesigner from './sections/SectionSuggestLocalDesigner/SectionSuggestLocalDesigner.vue'
import SectionSuggestTrips from './sections/SectionSuggestTrips/SectionSuggestTrips.vue'
import SectionTimeLine from './sections/SectionTimeLine/SectionTimeline.vue'
import SectionTripHighlights from './sections/SectionTripHighlights/SectionTripHighlights'
import TripPriceBox from './sections/SectionTripPriceBox/TripPriceBox'
import { useCurrenciesStore } from '~/store/pinia/currencies'
import { useTripPlanStore } from '~/store/pinia/tripPlan'
import { useAuthStore } from '~/store/pinia/auth'
import { useGeneralInfoStore } from '~/store/pinia/generalInfo'

const props = defineProps({
  loadingStatus: {
    type: Boolean,
    default: false,
  },
  agentContactApiLoaded: {
    type: Boolean,
    default: false,
  },
})
const MARGIN_TOP = 160
const MARGIN_TOP_MOBILE = 20
const ITEM_DEFAULT_EXPAND = [0, 1, 2]

const isExpired = inject('isExpired', false)

const domain = publicDomain()
const route = useRoute()
const router = useRouter()
const sectionTimeLineRef = ref(null)
const sectionInclusionsRef = ref(null)
const sectionDetailedReviewRef = ref(null)
const containerRef = ref(null)
const popupDownloadRef = ref(null)
const popupShareRef = ref(null)
const popupTripExpiredRef = ref(null)
const popupTermRef = ref(null)
const popupConfirmTravelDateRef = ref(null)
const multiStepsVisible = ref(false)
const topSectionEnquiryForm = ref(0)
const currenciesStore = useCurrenciesStore()
const tripPlanStore = useTripPlanStore()
const authStore = useAuthStore()
const generalStore = useGeneralInfoStore()

const windowWidth = computed(() => generalStore.generalData.windowWidth)
const isMobile = computed(() => windowWidth.value <= PHONE)
const isDesktop = computed(() => windowWidth.value >= DESKTOP)
const dataTrip = computed(() => tripPlanStore.dataTrip)
const dataEnquiry = computed(() => tripPlanStore.dataEnquiry)
const priceDisplay = computed(() => tripPlanStore.priceDisplayAPI)
const currenciesData = computed(() => currenciesStore.currenciesData /*store.state.currenciesStore.currenciesData*/)
const isBookableTrip = computed(() => route.name === 'trip-plan-bookable')
const isGalleryTrip = computed(() => route.name === 'trip-plan')
const showPopupClientFeedback = computed(() => !isExpired.value && !isCompleted.value && !dataTrip.value.is_booked)
const userInfo = computed(() => authStore.getUserInfo)
const showCountDown = computed(() => {
  if (!dataTrip.value?.is_defined_date) return false
  if (dataTrip.value?.is_booked || dataTrip.value?.is_cancelled) return false
  const timeBetween = daysBetween(new Date(), new Date(dataTrip.value?.valid_time), true)
  if (dataTrip.value?.type === 'bookable' && timeBetween <= 2 && !dataTrip.value?.is_fully_paid) {
    return true
  } else {
    return false
  }
})
const uniqueLocation = computed(() => {
  return dataTrip.value?.locations
})
const latestReviewWithContent = computed(() => {
  if (!sectionDetailedReviewRef.value) return null
  const reviewsLocalDesigner = sectionDetailedReviewRef.value.reviewsLocalDesigner
  return reviewsLocalDesigner?.items ? reviewsLocalDesigner?.items.find((item) => item.content) : null
})
const currencySelected = computed(() => currenciesStore.currencySelected /*store.state.currenciesStore.currencySelected*/)
const isCompleted = computed(() => tripPlanStore.isCompleted)
const bgPriceBoxClass = computed(() => {
  if (isCompleted.value) {
    return 'bg-cream-deep' // Wave 5B — completed-state price box: mint bg-green-branding → warm cream-deep tint
  }
  return 'bg-white'
})

watch(
  () => props.loadingStatus,
  (value) => {
    if (value) {
      scrollToHash(route.hash)
    }
  },
)

watch(dataTrip, async (value, oldValue) => {
  if (value?.id) {
    getTransaction()
  }
  if (value?.designer?.id && !isDeepEqual(value, oldValue)) {
    if (route.name !== 'trip-preview') heapTrack(value)
  }
})

function toggleModalShare(source) {
  if (!popupShareRef.value) return
  popupShareRef.value.toggleModalShare(source)
}

function toggleModalDJTerm(visible) {
  if (!popupTermRef.value) return
  popupTermRef.value.toggleModalDJTerm(visible)
}

function toggleModalDownload(source = 'trip-plan-actions-bar') {
  if (!popupDownloadRef.value) return
  popupDownloadRef.value.toggleModalDownload(source)
}

function toggleModalConfirmDate(visible) {
  if (!popupConfirmTravelDateRef.value) return
  popupConfirmTravelDateRef.value.toggleModalConfirm(visible)
}

function emitBook(source) {
  if (!popupTripExpiredRef.value) return
  popupTripExpiredRef.value.emitBook(source)
}

function customTrip(source_widget = 'Customize This Trip') {
  heapTrackViewEnquiry(source_widget)
  if (isMobile.value) {
    handleVisibleSubmitEnquiryForm(true)
  } else {
    router.push({
      name: 'enquiry-submit-trip',
      params: { trip: route.params.trip },
    })
  }
}

async function getTransaction() {
  if (dataTrip.value.is_booked) {
    try {
      const res = await tripApi.getTransaction(dataTrip.value.id)
      // store.commit('tripPlanStore/setTransactionData', { ...res.data.data, trip_id: dataTrip.value.id })
      tripPlanStore.setTransactionData({ ...res.data.data, trip_id: dataTrip.value.id })
    } catch (e) {
      console.log(e.message)
    }
  } else {
    // store.commit('tripPlanStore/setTransactionData', {})
    tripPlanStore.setTransactionData({})
  }
}

function scrollToInclusions() {
  if (!sectionInclusionsRef.value) return
  sectionInclusionsRef.value.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

function handleVisibleSubmitEnquiryForm(value) {
  multiStepsVisible.value = value
}

function getTopSectionEnquiryForm() {
  if (sectionTimeLineRef.value && containerRef.value) {
    return sectionTimeLineRef.value.offsetTop - containerRef.value.offsetTop
  }
  return 0
}

async function mapDefaultHeapParams(data) {
  let local_designer_chat_status = ''
  if (window.IS_BOT !== 1) {
    if (data?.designer?.id) local_designer_chat_status = (await onlineStatus.checkOnline(data?.designer?.id)) ? 'online' : 'offline'
  }
  const number_of_accommodation = data?.stay.slice(0, 2) || ''
  let number_of_transportation = 0
  let number_of_restaurant = 0
  let number_of_other_activity = 0
  let number_of_note = 0

  data?.itinerary.forEach((item) => {
    number_of_transportation += item.transportations.length
    number_of_restaurant += item.restaurants.length
    number_of_other_activity += item.activities.length
    number_of_note += item.notes.length
  })

  let heapParams = {
    name: data?.name,
    local_designer: data?.designer?.name,
    destination: data?.map_note?.destinations,
    display_currency: currencySelected.value.code,
    length_of_stay: data?.itinerary?.length,
    number_of_accommodation,
    number_of_transportation,
    number_of_restaurant,
    number_of_other_activity,
    number_of_note,
    meal_inclusion: data?.enjoy,
    activity_type: data?.activity_types.map((item) => item.name.replaceAll(',', '')).join(', '),
    local_designer_chat_status,
    number_of_review: data?.designer?.number_of_reviews,
    average_local_designer_rating: data?.designer?.average_rating || '',
    display_local_designer_rating: data?.designer?.rating || '',
    number_of_local_designer_review: data?.designer?.number_of_reviews,
    featured_review_creation_date: latestReviewWithContent.value?.date || '',
    featured_review_rating: latestReviewWithContent.value?.rating || '',
    highlight: data?.highlights?.map((item, index) => `${index + 1}. ${item}`).join('\n') || '',
    inclusion: data?.inclusions?.map((item) => item.replaceAll(',', '')).join(', '),
  }
  return heapParams
}

function heapTrackGalleryTrip(heapParams) {
  const privateHeapParams = {
    price_per_traveller: priceDisplay.value?.tracking?.perPerson,
    price_per_traveller_converted: priceDisplay.value?.tracking?.converted_perPerson,
  }
  const galleryParams = Object.assign({}, heapParams, privateHeapParams)
  debounceHeapTracking('View Gallery Trip Plan', galleryParams)
  debounceAmplitudeScriptTracking('View Gallery Trip Plan', galleryParams)
}

function heapTrackBookableTrip(heapParams) {
  const privateHeapParams = {
    is_simplified: dataTrip.value?.is_simplified,
    expired_at: dataTrip.value?.due_date ? formatDate(dataTrip.value?.due_date) : '',
    enquiry_id: dataTrip.value?.enquiry_id,
    trip_plan_id: dataTrip.value?.id,
    budget_currency: dataEnquiry.value?.how?.budgetCurrency,
    gross_budget: dataEnquiry.value?.enquiry_infos?.budget_total,
    gross_budget_converted: dataEnquiry.value?.enquiry_infos?.budget_total_converted,
    wholesale_price: priceDisplay.value?.tracking?.wholesale,
    wholesale_price_converted: priceDisplay.value?.tracking?.converted_wholesale,
    net_price: priceDisplay.value?.tracking?.net,
    net_price_converted: priceDisplay.value?.tracking?.converted_net,
    gross_price: priceDisplay.value?.tracking?.markup,
    gross_price_converted: priceDisplay.value?.tracking?.converted_markup,
    quoted_currency: dataTrip.value?.original_currency,
    usd_to_quoted_currency_rate: findExchangeRate(currenciesData.value, dataTrip.value?.original_currency).rate.toFixed(2),
    usd_to_display_currency_rate: currencySelected.value.rate.toFixed(2),
    number_of_traveller: dataTrip.value?.pax,
    // has_dates_matched: tripPlanSummary.value?.date?.matching,
    // has_number_of_travellers_matched: tripPlanSummary.value?.traveller?.matching,
    // has_accommodation_matched: tripPlanSummary.value?.stay?.matching,
    // has_total_price_matched: tripPlanSummary.value?.price?.matching,
    user_id: userInfo.value?.userId || '',
  }

  const bookableParams = Object.assign({}, heapParams, privateHeapParams)

  debounceHeapTracking('View Bookable Trip Plan', bookableParams)
  debounceAmplitudeScriptTracking('View Bookable Trip Plan', bookableParams)
}

function heapTrackViewEnquiry(sourceWidget) {
  debounceHeapTracking('View Enquiry', {
    source_widget: sourceWidget,
    source_type: route.name,
    type: domain === 'agent' ? 'b2b' : 'b2c',
    destination: dataTrip.value?.map_note?.destinations || '',
    trip_plan_name: route.name === 'trip-plan' ? dataTrip.value?.name : '',
    local_designer: dataTrip.value?.designer?.name,
  })
  debounceAmplitudeScriptTracking('View Enquiry', {
    source_widget: sourceWidget,
    source_type: route.name,
    type: domain === 'agent' ? 'b2b' : 'b2c',
    destination: dataTrip.value?.map_note?.destinations || '',
    trip_plan_name: route.name === 'trip-plan' ? dataTrip.value?.name : '',
    local_designer: dataTrip.value?.designer?.name,
  })
}

async function heapTrack(data) {
  if (process.server) return

  const heapParams = await mapDefaultHeapParams(data)

  if (route.name === 'trip-plan-bookable') {
    heapTrackBookableTrip(heapParams)
  } else {
    heapTrackGalleryTrip(heapParams)
  }
}

function dateString(date) {
  if (date !== '') {
    let d = new Date(date)
    let ye = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(d)
    let mo = new Intl.DateTimeFormat('en', { month: 'short' }).format(d)
    let da = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(d)
    return `${da} ${mo} ${ye}`
  }
  return ''
}
provide('showCountDown', showCountDown)

onMounted(async () => {
  await nextTick()
  topSectionEnquiryForm.value = getTopSectionEnquiryForm()
  if (dataTrip.value?.id) {
    getTransaction()
  }
  if (route.name !== 'trip-preview') heapTrack(dataTrip.value)
})
</script>

<style lang="scss" scoped>
@include phone {
  :deep(.sidebar) {
    padding-bottom: 0;
  }
}
</style>
